import {FormControl, FormGroup} from '@angular/forms';

import {map} from 'rxjs/operators';
import {BehaviorSubject, Observable} from 'rxjs';

import {
    ApiError,
    ApiResponse,
    CountryModel,
    HttpStatusCodesEnum,
    UserPhoneModel,
    UsersService
} from '@spa/api';

import {PhoneValidator} from '@spa/validators';

import {PhoneAutocompleteValueInterface} from '../../../../shared/widgets';
import {BaseControl, Helper, PhoneAutocompleteControl} from '../../../../shared/classes';

import {ContactDetailsFormErrorStatesEnum} from '../../enums';

import {ContactDetailComponentPhoneControl, ContactDetailPhoneSelector} from '../../../../../assets/descriptors';

export class ContactDetailsForm {
    private phoneNumber: string;
    private readonly countries: CountryModel[];
    private readonly usersService: UsersService;

    public form: FormGroup;
    public control: PhoneAutocompleteControl;
    public controlsObservable: BehaviorSubject<BaseControl<PhoneAutocompleteValueInterface>[]>;

    constructor(
        countries: CountryModel[],
        usersService: UsersService,
        phoneNumber: string = null
    ) {
        this.phoneNumber = phoneNumber;
        this.countries = !countries ? [] : countries;
        this.usersService = usersService;

        this.controlsObservable = new BehaviorSubject([]);
    }

    initForm(): void {
        const phoneCodes = this.countries.map((country: CountryModel) => country.phoneCode);

        this.form = new FormGroup({});
        this.control = ContactDetailComponentPhoneControl();

        if (this.phoneNumber) {
            const filteredPhoneCodes = this.countries.filter((country: CountryModel) => {
                const phone = this.phoneNumber;
                const regex = new RegExp('^\\' + country.phoneCode);

                return phone.match(regex) && (phone.match(regex)[0].length === country.phoneCode.length);
            });

            if (filteredPhoneCodes && filteredPhoneCodes.length > 0) {
                this.control.value = {
                    code: filteredPhoneCodes[0].phoneCode,
                    phone: this.phoneNumber.replace(filteredPhoneCodes[0].phoneCode, '')
                };
            }
        }

        this.control.countries = this.countries;
        this.control.validators.push(PhoneValidator(phoneCodes));

        this.controlsObservable.next([this.control]);

        this.form.addControl(this.control.name, new FormControl(this.control.value, this.control.validators));
    }

    validateForm(messageFunction: (controlLabel?: string) => { [key: string]: string; }): boolean {
        if (!this.form.valid) {
            const controlName = this.control.name;

            if (!this.form.controls[controlName]) {
                return false;
            }

            const formControl = this.form.controls[controlName];

            if (formControl.invalid) {
                const errorKeys = Object.keys(formControl.errors);

                const errors = errorKeys.map((key: string) => {
                    return messageFunction(this.control.placeholder)[key];
                });

                this.control.setErrors(errors);
            }

            return false;
        }

        return true;
    }

    createRequest(userId: number): Observable<ApiResponse<ApiError[] | { userId: number, phone: string }>> | null {
        const code = (this.form.value[ContactDetailPhoneSelector] as PhoneAutocompleteValueInterface).code;
        const phone = (this.form.value[ContactDetailPhoneSelector] as PhoneAutocompleteValueInterface).phone;
        const phoneNumber = this.phoneNumber;

        if (!phoneNumber || (phoneNumber && (phoneNumber !== (code + phone)))) {

            const model = new UserPhoneModel(
                userId,
                code,
                phone
            );

            return this.usersService.updatePhone(model)
                .pipe(
                    map((response: ApiResponse<ApiError[] | null>) => {
                        if (response.status !== HttpStatusCodesEnum.Ok) {
                            return Helper.errorsHelper().createErrorResponse<null>(
                                response,
                                ContactDetailsFormErrorStatesEnum.UserPhoneUpdateFailed
                            );
                        }
                        this.phoneNumber = code + phone;

                        return Helper.createSuccessResponse<{ userId: number, phone: string }>({userId, phone:  this.phoneNumber});
                    })
                );
        }

        return null;
    }
}
